import { useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Home, ArrowLeft, BookOpen, PenTool, Lightbulb, GraduationCap } from "lucide-react";
import SchoololaLogo from "@/assets/schoolola-logo.svg";
const notFoundStyles = `
@keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-12px); } }
@keyframes subtle-rotate { 0%, 100% { transform: rotate(-2deg); } 50% { transform: rotate(2deg); } }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
@keyframes pulse-subtle { 0%, 100% { box-shadow: 0 4px 14px -3px hsl(var(--primary) / 0.4); } 50% { box-shadow: 0 4px 20px -3px hsl(var(--primary) / 0.6); } }
@keyframes orbit { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
@keyframes sparkle { 0%, 100% { opacity: 0; transform: scale(0.5); } 50% { opacity: 1; transform: scale(1.2); } }
@keyframes twinkle { 0%, 100% { opacity: 0.2; transform: scale(0.8) rotate(0deg); } 50% { opacity: 1; transform: scale(1.1) rotate(180deg); } }
.animate-pulse-subtle { animation: pulse-subtle 2s ease-in-out infinite; }
`;

const NotFound = () => {
  const location = useLocation();
  const [showHint, setShowHint] = useState(false);
  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
    // Show hint after delay
    const timer = setTimeout(() => setShowHint(true), 1500);
    return () => clearTimeout(timer);
  }, [location.pathname]);
  return <><style>{notFoundStyles}</style><div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center px-4 overflow-hidden">
      {/* Floating animated icons */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <BookOpen className="absolute top-[15%] left-[10%] w-8 h-8 text-primary/20 animate-bounce" style={{
        animationDelay: '0s',
        animationDuration: '3s'
      }} />
        <PenTool className="absolute top-[25%] right-[15%] w-6 h-6 text-accent/20 animate-bounce" style={{
        animationDelay: '0.5s',
        animationDuration: '2.5s'
      }} />
        <Lightbulb className="absolute bottom-[30%] left-[20%] w-10 h-10 text-yellow-400/20 animate-bounce" style={{
        animationDelay: '1s',
        animationDuration: '3.5s'
      }} />
        <GraduationCap className="absolute bottom-[20%] right-[10%] w-8 h-8 text-primary/20 animate-bounce" style={{
        animationDelay: '1.5s',
        animationDuration: '2.8s'
      }} />
      </div>

      <div className="text-center max-w-md mx-auto relative z-10">
        {/* Animated logo and 404 */}
        <div className="mb-8 animate-fade-in">
          {/* Animated Schoolola Logo with layered effects */}
          <div className="mb-6 relative h-32 flex items-center justify-center">
            {/* Sparkle particles */}
            {[...Array(8)].map((_, i) => <div key={i} className="absolute w-1.5 h-1.5 rounded-full bg-yellow-400" style={{
            top: `${20 + Math.sin(i * 45 * Math.PI / 180) * 45}%`,
            left: `${50 + Math.cos(i * 45 * Math.PI / 180) * 40}%`,
            animation: `sparkle ${1.5 + i % 3 * 0.5}s ease-in-out infinite`,
            animationDelay: `${i * 0.2}s`,
            opacity: 0
          }} />)}
            {/* Extra sparkle stars */}
            {[...Array(6)].map((_, i) => <div key={`star-${i}`} className="absolute text-yellow-400/80" style={{
            top: `${15 + Math.random() * 70}%`,
            left: `${15 + Math.random() * 70}%`,
            animation: `twinkle ${2 + Math.random()}s ease-in-out infinite`,
            animationDelay: `${i * 0.3}s`,
            fontSize: `${8 + Math.random() * 6}px`
          }}>
                ✦
              </div>)}
            {/* Glow ring effect */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-28 h-28 rounded-full bg-primary/10 animate-ping" style={{
              animationDuration: '3s'
            }} />
            </div>
            {/* Secondary pulse ring */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-24 h-24 rounded-full border-2 border-primary/30 animate-pulse" style={{
              animationDuration: '2s'
            }} />
            </div>
            {/* Rotating orbit dots */}
            <div className="absolute inset-0 flex items-center justify-center animate-spin" style={{
            animationDuration: '8s'
          }}>
              
            </div>
            {/* Logo with float and subtle rotation */}
            <div className="relative z-10 animate-scale-in">
              <img src={SchoololaLogo} alt="Schoolola Tutoring" className="h-16 mx-auto drop-shadow-lg" style={{
              animation: 'float 3s ease-in-out infinite, subtle-rotate 6s ease-in-out infinite'
            }} />
            </div>
          </div>
          
          <div className="relative inline-block">
            <h1 className="text-8xl font-bold text-primary mb-2 animate-scale-in">
              4
              <span className="inline-block animate-bounce" style={{
              animationDuration: '1s'
            }}>
                <BookOpen className="inline w-16 h-16 mx-1" />
              </span>
              4
            </h1>
            {/* Pencil drawing underline */}
            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent animate-pulse" />
          </div>
          
          <h2 className="text-2xl font-semibold text-foreground mb-4 animate-fade-in" style={{
          animationDelay: '0.2s'
        }}>
            Oops! This Lesson Isn't in Our Curriculum
          </h2>
          
          <p className="text-muted-foreground text-lg leading-relaxed animate-fade-in" style={{
          animationDelay: '0.4s'
        }}>
            Looks like this page took an unexpected study break! Don't worry, even the best students sometimes take a wrong turn.
          </p>
        </div>
        
        {/* Animated hint box */}
        <div className={`mb-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl transition-all duration-500 ${showHint ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="flex items-center gap-3">
            <Lightbulb className="w-6 h-6 text-yellow-500 animate-pulse" />
            <p className="text-sm text-yellow-700 dark:text-yellow-300 text-left">
              <strong>Study Tip:</strong> Double-check the URL or head back to our homepage to find what you're looking for!
            </p>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{
        animationDelay: '0.6s'
      }}>
          <Button asChild size="lg" className="flex items-center gap-2 hover-scale">
            <Link to="/">
              <Home className="h-4 w-4" />
              Back to School
            </Link>
          </Button>
          
          <Button variant="outline" size="lg" onClick={() => window.history.back()} className="flex items-center gap-2 hover-scale">
            <ArrowLeft className="h-4 w-4" />
            Previous Page
          </Button>
        </div>
        
        <div className="mt-8 p-6 border border-border rounded-xl bg-card/50 backdrop-blur-sm animate-fade-in" style={{
        animationDelay: '0.8s'
      }}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <GraduationCap className="w-5 h-5 text-primary" />
            <span className="font-medium text-foreground">Need Help?</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Contact us at <a href="mailto:hello@schoolola.com.au" className="text-primary hover:underline">hello@schoolola.com.au</a> or call <a href="tel:0291617167" className="text-primary hover:underline">02 9161 7167</a>
          </p>
        </div>
      </div>
    </div></>;
};
export default NotFound;